// Words, not enums (UX §3 labels): "couldn't check", never "UNKNOWN".

import type { Check, HealthState, Source } from "./api";

export const STATE_WORD: Record<HealthState, string> = {
  ok: "ok",
  drifting: "drifting",
  dark: "dark",
  unknown: "couldn't check",
};

export const STATE_LETTER: Record<HealthState, string> = { ok: "G", drifting: "A", dark: "R", unknown: "?" };

/** C2 reason codes, in the team's words. Unrecognised codes say the check could not run. */
const REASON_WORDS: Record<string, string> = {
  permission_denied: "the monitor can't read this dataset yet",
  project_out_of_scope: "outside the monitor's reach",
  no_r1_evaluator: "no check exists for this kind of source yet",
  no_check_rule: "no check rule recorded",
  manual_source: "entered by hand, not checked",
  never_checked: "not checked yet",
  check_stale: "the last check is out of date",
  health_unreadable: "couldn't check",
  dataset_absent: "the dataset is missing",
  ledger_cycle_failed: "the last load failed",
};

/**
 * The short note on a couldn't-check Sources row, in the mock-up's words (S1:
 * "arrival check only", "no cadence recorded", "the check could not run").
 * R1 evaluates arrival of BigQuery tables only, so a source whose check kind it
 * cannot run reads "arrival check only". The full C2 reason words stay on
 * Source detail.
 */
const ROW_UNKNOWN_WORDS: Record<string, string> = {
  no_r1_evaluator: "arrival check only",
  no_check_rule: "no cadence recorded",
  no_freshness_threshold: "no cadence recorded",
};

/** A drifting row whose reason has no words yet still says what drifting means. */
const DRIFT_FALLBACK = "outside its usual range";

export function reasonWords(reason: string | null | undefined): string {
  if (!reason) return "the check could not run";
  return REASON_WORDS[reason] ?? "the check could not run";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-02" → "2 Sep". Anything unparseable is returned as given, never invented. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
}

/** Whole days between two ISO dates (for "dark 4 days"). Null if either is missing. */
export function daysBetween(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const a = Date.parse(from.slice(0, 10));
  const b = Date.parse(to.slice(0, 10));
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * The grey note on a Sources row (mock-up S1: "dark 4 days · back by 12 Sep").
 * Reads what C2 said; computes nothing new. The back-by date is C3's, which is
 * not live in R1, so a dark row says no date is committed, as Source detail does.
 */
export function rowNote(source: Source, healthReadable: boolean): string {
  if (!healthReadable) return "couldn't check";
  const { state, reason, check_date } = source.health;
  if (state === "dark") {
    const days = daysBetween(source.dark_since, check_date);
    const dark = days === null ? "dark" : `dark ${days} ${days === 1 ? "day" : "days"}`;
    return `${dark} · no date committed`;
  }
  if (state === "drifting") return reason && REASON_WORDS[reason] ? REASON_WORDS[reason] : DRIFT_FALLBACK;
  if (state === "ok") return check_date ? `checked ${shortDate(check_date)}` : "ok";
  return (reason && ROW_UNKNOWN_WORDS[reason]) || "the check could not run";
}

export type OwnerGroup = { key: string; label: string; owner: string | null; sources: Source[] };

/** UNOWNED pinned first (UX §3), then one group per owner, alphabetical. */
export function groupByOwner(sources: Source[]): OwnerGroup[] {
  const unowned = sources.filter((s) => !s.owner_email);
  const byOwner = new Map<string, Source[]>();
  for (const s of sources) {
    if (!s.owner_email) continue;
    byOwner.set(s.owner_email, [...(byOwner.get(s.owner_email) ?? []), s]);
  }
  const groups: OwnerGroup[] = [{ key: "__unowned", label: "No owner", owner: null, sources: sortRows(unowned, UNOWNED_ORDER) }];
  for (const owner of [...byOwner.keys()].sort()) {
    groups.push({ key: owner, label: personLabel(owner), owner, sources: sortRows(byOwner.get(owner) ?? [], ROW_ORDER) });
  }
  return groups;
}

/**
 * Row order inside a group, as the mock-up lists it. An owner's group runs dark, drifting, ok, then
 * couldn't check (UX §6 wireframe). The unowned group previews only three rows, and the mock-up fills
 * them with couldn't-check sources, so there couldn't check ranks above ok.
 */
type RowOrder = Record<HealthState, number>;
const ROW_ORDER: RowOrder = { dark: 0, drifting: 1, ok: 2, unknown: 3 };
const UNOWNED_ORDER: RowOrder = { dark: 0, drifting: 1, unknown: 2, ok: 3 };

function sortRows(rows: Source[], order: RowOrder): Source[] {
  return [...rows].sort(
    (a, b) => order[a.health.state] - order[b.health.state] || displayName(a).localeCompare(displayName(b)),
  );
}

export function displayName(source: Pick<Source, "name" | "source_key">): string {
  return source.name || source.source_key;
}

/** Marketplace codes a source name can end in ("Amazon Sales & Traffic UK"). */
const MARKETPLACES = new Set([
  "UK", "EU", "US", "CA", "MX", "BR", "DE", "FR", "IT", "ES", "NL", "BE", "SE", "PL", "IE", "TR",
  "AE", "SA", "EG", "IN", "JP", "AU", "SG",
]);

/**
 * Splits a trailing marketplace code off a source name, so a long name can truncate
 * mid-string while the suffix stays visible (UX §5: "never the marketplace suffix").
 */
export function splitMarketplace(name: string): { body: string; suffix: string | null } {
  const m = /^(.*\S)\s+([A-Z]{2})$/.exec(name);
  return m && MARKETPLACES.has(m[2]) ? { body: m[1], suffix: m[2] } : { body: name, suffix: null };
}

/** "fiona.x@ketosource.co" → "Fiona X". Derived from the email, the same way the shell does. */
export function personLabel(email: string): string {
  const local = email.split("@", 1)[0];
  return local
    .replace(/\./g, " ")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");
}

export function counts(sources: Source[], healthReadable: boolean) {
  const c = { total: sources.length, ok: 0, drifting: 0, dark: 0, unknown: 0, unowned: 0 };
  for (const s of sources) {
    c[healthReadable ? s.health.state : "unknown"] += 1;
    if (!s.owner_email) c.unowned += 1;
  }
  return c;
}

/** Restored = ok now, with a dark check still in the recent history. The first ok after it. */
export function backSince(checks: Check[]): string | null {
  let since: string | null = null;
  for (const check of checks) {
    if (check.state === "ok") since = check.check_date;
    else if (check.state === "dark") return since;
    else return null;
  }
  return null;
}
